/* eslint-disable ember/no-incorrect-calls-with-inline-anonymous-functions, ember/no-computed-properties-in-native-classes */
/* eslint-disable ember/no-runloop */
import Controller, { inject as controller } from '@ember/controller';
import { action, computed } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { RESIZE_EVENTS } from 'lumen/services/resize';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import { SORT_ORDER } from 'lumen/utils/constants/general-constants';
import {
  COLUMN_PROPERTIES,
  SUBTYPE_DELIMITER,
  TYPE_ID_BEST_FLEX,
  TYPE_ID_CANCELLATION,
  TYPE_ID_NON_REF,
  TYPE_ID_SEMI_FLEX,
} from 'lumen/utils/constants/rate-strategy-constants';
import { sortHelper } from 'lumen/utils/strategy/detail-view-helpers';
import hotelinfoSortHelper from 'lumen/utils/tables/sort-helpers';

export default class HotelStrategyStrategyDetailBaseController extends Controller {
  queryParams = ['sortBy', 'sortDirection'];

  @service('hotel-and-market') hotelAndMarketService;
  @service resize;
  // Used for usage tracking of e.g. exports (can also be in hbs files instead of JS)
  @service router;
  @service rsSelections;

  @controller('hotel.strategy') parentController;

  @tracked height = 600;
  // Initial sort order for PRISMTable API
  @tracked initialSorts = [
    {
      valuePath: this.sortBy,
      isAscending: this.isAscendingSort,
    },
  ];
  @tracked sortBy = DEFAULT_QP_VALUES.hotel.strategy.sortBy;
  @tracked sortDirection = DEFAULT_QP_VALUES.hotel.strategy.sortDirection;
  @tracked width = 600;

  // Must be defined in all controllers extending StrategyDetailBaseController
  typeId = null;

  get canRender() {
    return !this.isLoading && this.hasRSData;
  }

  get hasRSData() {
    return Object.keys(this.typesDataPerId).length > 0;
  }

  get isAscendingSort() {
    return this.sortDirection === SORT_ORDER.ASCENDING;
  }

  get isLoading() {
    return this.parentController.isLoading;
  }

  @computed('hotelAndMarketService.ownHotelAndAllCompetitors')
  get ownHotelAndAllCompetitors() {
    return this.hotelAndMarketService.ownHotelAndAllCompetitors;
  }

  @computed('rsSelections.selectedCompetitorIDs')
  get selectedCompetitorIDs() {
    return this.rsSelections?.selectedCompetitorIDs;
  }

  @computed('ownHotelAndAllCompetitors', 'selectedCompetitorIDs') // Necessary to observe selectedCompetitorIDs in rsSelections
  get selectedCompetitors() {
    return this.ownHotelAndAllCompetitors.filter(
      (hotelinfo) =>
        this.hotelAndMarketService.isOwnHotelInfo(hotelinfo) ||
        this.selectedCompetitorIDs.includes(hotelinfo.id),
    );
  }

  get subtypes() {
    // Return all cancellation subtypes for the cancellation detail view
    if (this.hasRSData && this.typeId === TYPE_ID_CANCELLATION) {
      return [
        ...this.typesDataPerId[TYPE_ID_BEST_FLEX].filteredSubtypes,
        ...this.typesDataPerId[TYPE_ID_SEMI_FLEX].filteredSubtypes,
        ...this.typesDataPerId[TYPE_ID_NON_REF].filteredSubtypes,
      ];
    }
    if (this.typeData) {
      return this.typeData.filteredSubtypes;
    }
    return [];
  }

  get typeData() {
    return this.typesDataPerId[this.typeId];
  }

  get typesDataPerId() {
    return this.parentController?.typesDataPerId;
  }

  constructor() {
    super(...arguments);
    this.resize.registerResizeEvent(
      RESIZE_EVENTS.MODAL,
      this.resetContainerDimensions.bind(this),
    );
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.resize.unregisterResizeEvent(RESIZE_EVENTS.MODAL);
  }

  @action
  onTableSort(sorts) {
    this.sortBy = sorts[0].valuePath;
    this.sortDirection = sorts[0].isAscending
      ? SORT_ORDER.ASCENDING
      : SORT_ORDER.DESCENDING;
  }

  @action
  redirectOnClose() {
    this.router.transitionTo('hotel.strategy');
  }

  @action
  resetContainerDimensions() {
    scheduleOnce('afterRender', this, () => {
      this.height = document.querySelector('.modal--stretched')?.offsetHeight;
      this.width = document.querySelector('.modal--stretched')?.offsetWidth;
    });
  }

  @action
  sortFunction(rowA, rowB) {
    /**
     * Sort function for ember-table
     * @param {Object} rowA - First row to compare
     * @param {Object} rowB - Second row to compare
     * @returns {Number} -1, 0, or 1
     */
    const sortKey = this.sortBy;
    const splitSortKey = sortKey.split(SUBTYPE_DELIMITER);
    const subtype = splitSortKey[0]; // e.g. best_flex
    const column = splitSortKey[1]; // e.g. rate

    // Custom sort for hotelinfo column
    if (sortKey === COLUMN_PROPERTIES.HOTELINFO) {
      return hotelinfoSortHelper(
        rowA,
        rowB,
        this.sortDirection,
        this.ownHotelAndAllCompetitors,
      );
    }

    /**
     * Sort function for all other columns
     * Check if sortKey QP is valid for the current table
     * by checking if the first row has the sortKey property (e.g. best_flex-rate) in the case of a simple object
     * or the subtype property (e.g. best_flex) in the case of nested objects in row data
     */
    if (
      sortKey !== COLUMN_PROPERTIES.HOTELINFO &&
      (rowA[sortKey] ?? rowA[subtype])
    ) {
      // Use the sortHelper function for all other columns
      return sortHelper(rowA, rowB, subtype, column, this.sortDirection);
    }
    return 0;
  }
}
